import { isTauri } from '@tauri-apps/api/core';
import { desktopNotify } from './desktop';
import { apiGet } from './api';
import { getSession, sessionScope } from './auth';
import type { ServerMessage } from './messages';

export interface TabActivity { roomId?: string; visible: boolean; at: number }
const TAB_PREFIX = 'ailechat:activity-tab:';
export function activityRoom(path: string): string | undefined { return path.match(/^\/rooms\/([^/]+)$/)?.[1]; }
export function trackActivityTab(scope: string, tabId: string, value: TabActivity | null): void {
  try { const key = `${TAB_PREFIX}${scope}:${tabId}`; if (value) localStorage.setItem(key, JSON.stringify(value)); else localStorage.removeItem(key); } catch { /* Storage unavailable: suppress notifications below. */ }
}
export function roomVisibleInAnyTab(scope: string, roomId: string, now = Date.now()): boolean {
  for (let index = localStorage.length - 1; index >= 0; index--) {
    const key = localStorage.key(index);
    if (!key?.startsWith(`${TAB_PREFIX}${scope}:`)) continue;
    try {
      const tab = JSON.parse(localStorage.getItem(key) || 'null') as TabActivity | null;
      if (!tab || now - tab.at > 30_000) localStorage.removeItem(key);
      else if (tab.visible && tab.roomId === roomId) return true;
    } catch { /* Ignore malformed tab metadata. */ }
  }
  return false;
}

/** Web Locks serialize delivery across tabs; only opaque IDs and timestamps are stored. */
export async function notifyActivityOnce(scope: string, roomId: string, messageId: string): Promise<void> {
  const native = isTauri();
  if (!native && (typeof Notification === 'undefined' || Notification.permission !== 'granted')) return;
  // Browsers without an origin-wide lock cannot safely guarantee single delivery.
  if (!navigator.locks) return;
  await navigator.locks.request(`ailechat:notification:${scope}:${messageId}`, async () => {
    if (sessionScope(getSession()) !== scope) return;
    const storageKey = `ailechat:notification-delivered:${scope}`;
    let delivered: Record<string, number>;
    try { delivered = JSON.parse(localStorage.getItem(storageKey) || '{}'); } catch { return; }
    const now = Date.now();
    if (delivered[messageId]) return;
    delivered = Object.fromEntries(Object.entries(delivered).filter(([, at]) => now - at < 86_400_000).slice(-499));
    const markDelivered = () => { delivered[messageId] = now; localStorage.setItem(storageKey, JSON.stringify(delivered)); };
    if (roomVisibleInAnyTab(scope, roomId)) { markDelivered(); return; }
    const [result, profile, preference] = await Promise.all([
      apiGet<{message: ServerMessage}>(`/v1/messages/${encodeURIComponent(messageId)}`),
      apiGet<{user: {preferences?: {notifications?: boolean}}}>('/v1/me/profile'),
      apiGet<{notification_level: string; muted_until?: number | null}>(`/v1/rooms/${encodeURIComponent(roomId)}/preferences`),
    ]);
    const session = getSession(), message = result.message;
    if (sessionScope(session) !== scope || message.room_id !== roomId || message.deleted_at || message.user_id === session?.user.id) return;
    if (profile.user.preferences?.notifications === false || (preference.muted_until ?? 0) > Date.now()) return;
    const mentioned = message.mentions?.some(mention => mention.target_id === session?.user.id || ['here', 'channel'].includes(mention.mention_type));
    if (preference.notification_level !== 'all' && !(preference.notification_level === 'mentions' && mentioned)) return;
    if (roomVisibleInAnyTab(scope, roomId)) { markDelivered(); return; }
    const body = message.body_md.slice(0, 180);
    if (native) await desktopNotify('新しいメッセージ', body);
    else {
      const notification = new Notification('新しいメッセージ', {body, tag: `${scope}:${messageId}`});
      notification.onclick = () => { if (sessionScope(getSession()) === scope) { window.focus(); window.location.assign(`/rooms/${encodeURIComponent(roomId)}?message=${encodeURIComponent(messageId)}`); } notification.close(); };
    }
    markDelivered();
  });
}

/**
 * Task assignment (activity event task.assigned, sent once per assignment by the API queue). Same delivery rules as
 * messages: browser permission / desktop app, one delivery across tabs keyed by the server's notification_id, and the
 * profile-wide notification switch. Room mute and "通知しない" were already applied by the server before sending.
 * The task is read through /v1/tasks/:id, so a task the user can no longer open produces no notification.
 */
export async function notifyTaskAssignedOnce(scope: string, taskId: string, notificationId: string, actorId?: string): Promise<void> {
  const native = isTauri();
  if (!native && (typeof Notification === 'undefined' || Notification.permission !== 'granted')) return;
  if (!navigator.locks) return;
  await navigator.locks.request(`ailechat:notification:${scope}:${notificationId}`, async () => {
    if (sessionScope(getSession()) !== scope) return;
    const storageKey = `ailechat:notification-delivered:${scope}`;
    let delivered: Record<string, number>;
    try { delivered = JSON.parse(localStorage.getItem(storageKey) || '{}'); } catch { return; }
    const now = Date.now();
    if (delivered[notificationId]) return;
    delivered = Object.fromEntries(Object.entries(delivered).filter(([, at]) => now - at < 86_400_000).slice(-499));
    const [result, profile] = await Promise.all([
      apiGet<{task: {id: string; title: string; workspace_id: string; assignee: string | null; due_at: number | null}}>(`/v1/tasks/${encodeURIComponent(taskId)}`),
      apiGet<{user: {preferences?: {notifications?: boolean}}}>('/v1/me/profile'),
    ]);
    const session = getSession(), task = result.task;
    if (sessionScope(session) !== scope || task.assignee !== session?.user.id) return;
    if (profile.user.preferences?.notifications === false) return;
    let actorName = 'メンバー';
    if (actorId) {
      try {
        const members = await apiGet<{members: Array<{id: string; display_name?: string | null; email?: string | null}>}>(`/v1/workspaces/${encodeURIComponent(task.workspace_id)}/members`);
        const actor = members.members.find(member => member.id === actorId);
        actorName = actor?.display_name || actor?.email || actorName;
      } catch { /* Name lookup is cosmetic; keep the generic label. */ }
    }
    const title = task.title.length > 80 ? `${task.title.slice(0, 79)}…` : task.title;
    const due = task.due_at ? `（期限: ${new Date(task.due_at).toLocaleString('ja-JP', {year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit'})}）` : '';
    const body = `${actorName}さんがあなたをタスク「${title}」の担当者にしました${due}`;
    if (native) await desktopNotify('タスクの担当者になりました', body);
    else {
      const notification = new Notification('タスクの担当者になりました', {body, tag: `${scope}:${notificationId}`});
      notification.onclick = () => { if (sessionScope(getSession()) === scope) { window.focus(); window.location.assign(`/tasks?task=${encodeURIComponent(taskId)}`); } notification.close(); };
    }
    delivered[notificationId] = now; localStorage.setItem(storageKey, JSON.stringify(delivered));
  });
}
